import useLayerListeners from './useLayerListeners';
import { Ref, WatchStopHandle } from 'vue';
import omit from 'lodash/omit';
import { LAYER_CONFIG_OMIT_PROP } from '../constants';

export default function useModelLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<ModelLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config || !TMap.model?.GLTFModel) return;

  const { id: layerId } = layerInfo.value;
  const { src } = toRefs(reactive(layerInfo.value.config));
  const options = omit(layerInfo.value.config, [...LAYER_CONFIG_OMIT_PROP]);

  let unwatchList: WatchStopHandle[] | null = [];

  if (!src.value) {
    unwatchList.push(watch(() => layerInfo.value.config.src, () => {
      unwatchList?.forEach(unwatch => unwatch());
      unwatchList = null;
      useModelLayer(mapInstance, layerInfo);
    }));
    return;
  }

  const modelLayer = new TMap.model.GLTFModel({
    id: layerId,
    map: mapInstance,
    url: src.value,
    ...options,
    position: new TMap.LatLng(options.position?.lat, options.position?.lng),
  });

  Object.keys(options).forEach((key) => {
    unwatchList?.push(watch(() => layerInfo.value.config[key], (newVal) => {
      if (key === 'src') {
        modelLayer?.unmount();
        useModelLayer(mapInstance, layerInfo);
      } else {
        modelOptionsHandlers[key](newVal, modelLayer);
      }
    }, { deep: true }));
  });

  // 用户传入的事件回调在这里处理
  unwatchList.push(watchEffect(() => {
    useLayerListeners(modelLayer, [
      { eventName: 'click', callbackList: layerInfo.value.config.onClick },
      { eventName: 'loaded', callbackList: layerInfo.value.config.onLoaded },
      { eventName: 'load_failed', callbackList: layerInfo.value.config.onLoadFailed },
    ]);
  }));

  modelLayer.unmount = (): void => {
    modelLayer?.destroy();
    unwatchList?.forEach(unwatch => unwatch());
    unwatchList = null;
  };

  return modelLayer;
}

const modelOptionsHandlers = {
  position(val: LatLng, layerInstance: LooseObject): void {
    layerInstance.setPosition(new TMap.LatLng(val.lat, val.lng));
  },
  anchor(val: ModelLayerConfig['anchor'], layerInstance: LooseObject): void {
    layerInstance.setAnchor(val);
  },
  rotation(val: ModelLayerConfig['rotation'], layerInstance: LooseObject): void {
    layerInstance.setRotation(val);
  },
  scale(val: number, layerInstance: LooseObject): void {
    layerInstance.setScale(val);
  },
};
